import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";

type ConsentRequest = {
  id: string;
  clientName: string;
  redirectHost: string;
  expiresAt: string;
  staffName: string;
};

type Connection = {
  id: string;
  clientName: string;
  connectedAt: string;
  lastRefreshedAt: string;
};

function requestIdFromLocation(): string | null {
  return new URLSearchParams(window.location.search).get("request");
}

export default function ConnectedAppsPage() {
  usePageTitle("Connected AI assistants");
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const requestId = requestIdFromLocation();
  const isAdmin = user?.role === "admin";
  const adminName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Admin";
  const [actionError, setActionError] = useState<string | null>(null);
  const [pending, setPending] = useState<"approve" | "deny" | null>(null);

  const consent = useQuery<ConsentRequest>({
    queryKey: ["/api/oauth/requests", requestId],
    enabled: isAdmin && !!requestId,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/oauth/requests/${encodeURIComponent(requestId!)}`);
      return res.json();
    },
    retry: false,
  });

  const connections = useQuery<Connection[]>({
    queryKey: ["/api/oauth/connections"],
    enabled: isAdmin,
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/oauth/connections");
      return res.json();
    },
  });

  async function decide(kind: "approve" | "deny") {
    if (!requestId) return;
    setActionError(null);
    setPending(kind);
    try {
      const res = await apiRequest("POST", `/api/oauth/requests/${encodeURIComponent(requestId)}/${kind}`);
      const body = (await res.json()) as { redirectTo?: string };
      if (body.redirectTo) window.location.assign(body.redirectTo);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Could not update this request.");
      setPending(null);
    }
  }

  async function revoke(id: string) {
    setActionError(null);
    await apiRequest("DELETE", `/api/oauth/connections/${encodeURIComponent(id)}`);
    await queryClient.invalidateQueries({ queryKey: ["/api/oauth/connections"] });
  }

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-xl">
        <Alert variant="destructive">
          <AlertDescription>Connected AI assistants are limited to administrators.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const clientName = consent.data?.clientName;

  return (
    <div className="p-6 max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Connected AI assistants</h1>
        <p className="text-sm text-muted-foreground">
          Read-only access to Dottie reports and records for {adminName}.
        </p>
      </div>

      {actionError && (
        <Alert variant="destructive">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      {requestId && (
        <Card>
          <CardHeader>
            <CardTitle>
              {consent.isLoading
                ? "Checking the connection request"
                : consent.isError
                  ? "This request is no longer valid"
                  : `${clientName} wants to read Dottie as ${consent.data?.staffName || adminName}`}
            </CardTitle>
            <CardDescription>
              {consent.data && `After you approve, the browser returns to ${consent.data.redirectHost}. This grants read-only access. `}
              Approve only a request you just started from your assistant.
            </CardDescription>
          </CardHeader>
          {consent.data && (
            <CardContent className="flex gap-2">
              <Button onClick={() => decide("approve")} disabled={pending !== null} data-testid="button-approve-connection">
                {pending === "approve" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Approve
              </Button>
              <Button variant="outline" onClick={() => decide("deny")} disabled={pending !== null} data-testid="button-deny-connection">
                Deny
              </Button>
            </CardContent>
          )}
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Connections</CardTitle>
          <CardDescription>Each connection is separate. Revoking one leaves the others working.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {connections.isLoading && <p className="text-sm text-muted-foreground">Loading connections…</p>}
          {connections.data?.length === 0 && <p className="text-sm text-muted-foreground">No connections yet.</p>}
          {connections.data?.map((connection) => (
            <div key={connection.id} className="flex items-center justify-between gap-4 border rounded-md p-3">
              <div>
                <p className="font-medium">{connection.clientName}</p>
                <p className="text-xs text-muted-foreground">
                  Connected {new Date(connection.connectedAt).toLocaleString()}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => revoke(connection.id)}>
                Revoke
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
